class FeaturedRecommendProductsSlider extends HTMLElement {
  connectedCallback() {
    this.scheduleBind();
  }

  scheduleBind() {
    cancelAnimationFrame(this.bindFrame);
    this.bindFrame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      if (!this.querySelector('items-scroll') || !this.querySelector('[data-items]')) {
        this.watchChildren();
        return;
      }
      this.bind();
    });
  }

  watchChildren() {
    this.childObserver?.disconnect();
    this.childObserver = new MutationObserver(() => {
      if (!this.querySelector('items-scroll') || !this.querySelector('[data-items]')) return;
      this.childObserver.disconnect();
      this.childObserver = null;
      this.bind();
    });
    this.childObserver.observe(this, { childList: true, subtree: true });
  }

  bind() {
    this.scroller = this.querySelector('items-scroll');
    this.items = this.querySelector('[data-items]');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (Number(this.dataset.autoplaySpeed) || 3) * 1000;
    this.hovered = false;
    this.pointerDown = false;
    this.focused = false;
    this.inView = false;
    this.programmatic = false;

    if (!this.scroller || !this.items) return;

    this.stopAutoplay();
    this.observer?.disconnect();
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.stampNavigation();
    this.navObserver = new MutationObserver(() => this.stampNavigation());
    this.navObserver.observe(this.scroller, { childList: true, subtree: true });

    this.items.addEventListener('scroll', () => {
      if (!this.programmatic) this.restartAutoplay();
    }, { passive: true, signal });
    this.scroller.addEventListener('on:items-scroll:change-state', () => this.syncAutoplay(), { signal });

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.hovered = true;
      this.stopAutoplay();
    }, { signal });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.hovered = false;
      this.scheduleSync();
    }, { signal });
    this.addEventListener('pointerdown', () => {
      this.pointerDown = true;
      this.stopAutoplay();
    }, { signal });
    this.addEventListener('pointerup', () => {
      this.pointerDown = false;
      this.scheduleSync();
    }, { signal });
    this.addEventListener('pointercancel', () => {
      this.pointerDown = false;
      this.scheduleSync();
    }, { signal });
    this.addEventListener('focusin', (event) => {
      if (event.target.closest('.navigation__button')) return;
      this.focused = true;
      this.stopAutoplay();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.focused = false;
      this.scheduleSync();
    }, { signal });

    window.addEventListener('resize', () => this.syncAutoplay(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal });

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.prefersReducedMotion.addEventListener('change', () => this.syncAutoplay(), { signal });

    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.syncAutoplay();
    }, { threshold: 0.4 });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.bindFrame);
    this.childObserver?.disconnect();
    this.childObserver = null;
    this.navObserver?.disconnect();
    this.navObserver = null;
    this.stopAutoplay();
    this.observer?.disconnect();
    this.observer = null;
    clearTimeout(this.programmaticTimer);
    clearTimeout(this.syncTimer);
    this.abort?.abort();
  }

  stampNavigation() {
    const slotId = this.dataset.slotId || '';
    const sectionName = this.dataset.sectionName || '';
    this.querySelectorAll('[data-navigation-prev], [data-navigation-next]').forEach((button) => {
      if (button.getAttribute('data-sensors-click-event-name')) return;
      const subModule = button.hasAttribute('data-navigation-prev') ? 'prev' : 'next';
      button.setAttribute('data-sensors-click-event-name', 'slot_click');
      button.setAttribute('data-sensors-click-property-slot_id', slotId);
      button.setAttribute('data-sensors-click-property-section_name', sectionName);
      button.setAttribute('data-sensors-click-property-sub_module', subModule);
      button.setAttribute('data-sensors_click', '');
    });
  }

  getNextSlide() {
    const slides = [...this.items.children];
    if (!slides.length) return null;

    let current = slides[0];
    for (const slide of slides) {
      if (slide.offsetLeft <= this.items.scrollLeft + 4) current = slide;
      else break;
    }
    return current.nextElementSibling;
  }

  canAutoplay() {
    if (!this.autoplayEnabled || !this.items || !this.inView) return false;
    if (this.prefersReducedMotion?.matches) return false;
    if (document.hidden || this.hovered || this.pointerDown || this.focused) return false;
    return this.items.scrollWidth > this.items.clientWidth + 2;
  }

  scheduleSync() {
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.syncAutoplay(), 50);
  }

  syncAutoplay() {
    if (!this.canAutoplay()) {
      this.stopAutoplay();
      return;
    }
    if (this.timer) return;
    this.timer = window.setInterval(() => this.autoplayStep(), this.autoplaySpeed);
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.syncAutoplay();
  }

  stopAutoplay() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = 0;
  }

  autoplayStep() {
    if (!this.canAutoplay()) {
      this.stopAutoplay();
      return;
    }

    const maxScroll = this.items.scrollWidth - this.items.clientWidth;
    if (maxScroll <= 2) return;

    this.programmatic = true;
    clearTimeout(this.programmaticTimer);
    this.programmaticTimer = setTimeout(() => {
      this.programmatic = false;
    }, 800);

    const finish = () => {
      this.programmatic = false;
      clearTimeout(this.programmaticTimer);
    };

    if (this.items.scrollLeft >= maxScroll - 2) {
      this.items.scrollTo({ left: 0, behavior: 'auto' });
      finish();
      return;
    }

    const next = this.getNextSlide();
    if (!next || typeof this.scroller.smoothScrollItems !== 'function') {
      this.items.scrollTo({ left: 0, behavior: 'auto' });
      finish();
      return;
    }

    this.scroller.smoothScrollItems(next.offsetLeft).then(finish, finish);
  }
}

if (!customElements.get('featured-recommend-products-slider')) {
  customElements.define('featured-recommend-products-slider', FeaturedRecommendProductsSlider);
}
